import React, { useState } from 'react';
import { OptionsVolumeChart } from './OptionsVolumeChart';
import { VolumeTimeframeSelector } from './VolumeTimeframeSelector';
import { VolumeMetricSelector } from './VolumeMetricSelector';
import { useOptionsVolumeData } from '../hooks/useOptionsVolumeData';
import { VolumeTimeframe, VolumeMetricKey } from '../types/StockData';

interface OptionsVolumeOverTimePageProps {
  ticker: string;
  onTickerChange: (ticker: string) => void;
}

export const OptionsVolumeOverTimePage: React.FC<OptionsVolumeOverTimePageProps> = ({
  ticker,
  onTickerChange,
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<VolumeTimeframe>('1W');
  const [selectedMetric, setSelectedMetric] = useState<VolumeMetricKey>('call_volume');
  const [inputTicker, setInputTicker] = useState(ticker);

  const { data, loading, error, refetch } = useOptionsVolumeData(ticker, selectedTimeframe);

  const handleTickerSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputTicker.trim()) {
      onTickerChange(inputTicker.trim().toUpperCase());
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (!data || data.length === 0) return null;

    // derive value for base and combined metrics
    const computeMetricValue = (d: any, key: VolumeMetricKey): number => {
      switch (key) {
        case 'netDeltaFlow':
          return Number(d.callDelta ?? 0) + Number(d.putDelta ?? 0);
        case 'netGammaFlow':
          return Number(d.callGamma ?? 0) + Number(d.putGamma ?? 0);
        case 'netCharmFlow':
          return Number(d.callCharm ?? 0) + Number(d.putCharm ?? 0);
        case 'netVannaFlow':
          return Number(d.callVanna ?? 0) + Number(d.putVanna ?? 0);
        default:
          return Number(d[key] ?? 0);
      }
    };

    const isNumber = (v: number | undefined | null): v is number => v !== null && v !== undefined && Number.isFinite(v);
    const values = data.map(d => computeMetricValue(d, selectedMetric)).filter(isNumber);
    if (values.length === 0) return null;

    const total = values.reduce((sum, val) => sum + val, 0);
    const average = total / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);

    return { total, average, max, min, count: values.length };
  }, [data, selectedMetric]);

  const formatNumber = (value: number): string => {
    if (Math.abs(value) >= 1e9) {
      return `${(value / 1e9).toFixed(2)}B`;
    } else if (Math.abs(value) >= 1e6) {
      return `${(value / 1e6).toFixed(2)}M`;
    } else if (Math.abs(value) >= 1e3) {
      return `${(value / 1e3).toFixed(2)}K`;
    }
    return value.toFixed(0);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Options Volume Over Time</h1>
              <p className="text-gray-600 mt-1">
                Analyze options volume patterns and trends across different time periods
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Ticker Search */}
              <form onSubmit={handleTickerSubmit} className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputTicker}
                  onChange={(e) => setInputTicker(e.target.value.toUpperCase())}
                  placeholder="Enter ticker..."
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Search
                </button>
              </form>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <VolumeTimeframeSelector
            selectedTimeframe={selectedTimeframe}
            onTimeframeChange={setSelectedTimeframe}
          />
          
          {/* Summary Statistics */}
          {summaryStats && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Summary Statistics
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-600">
                    {formatNumber(summaryStats.average)}
                  </div>
                  <div className="text-xs text-gray-500">Average</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600">
                    {formatNumber(summaryStats.max)}
                  </div>
                  <div className="text-xs text-gray-500">Maximum</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-red-600">
                    {formatNumber(summaryStats.min)}
                  </div>
                  <div className="text-xs text-gray-500">Minimum</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-600">
                    {summaryStats.count}
                  </div>
                  <div className="text-xs text-gray-500">Data Points</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-800 font-medium">Error loading data: {error}</span>
            </div>
          </div>
        )}

        {/* Chart */}
        <OptionsVolumeChart
          data={data}
          selectedMetric={selectedMetric}
          ticker={ticker}
          timeframe={selectedTimeframe}
          isLoading={loading}
        />

        {/* Metric Selector */}
        <VolumeMetricSelector
          selectedMetric={selectedMetric}
          onMetricChange={setSelectedMetric}
        />
      </div>
    </div>
  );
};
